import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import { PxDropdownSc } from 'components/final-form/Select';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PxGrid from 'components/layout/PxGrid';
import LoaderComponent from 'components/LoaderComponent';
import { FormApi } from 'final-form';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { Component, Fragment } from 'react';
import DayPicker from 'react-day-picker';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, DropdownProps, Grid, Header, Modal } from 'semantic-ui-react';
import { getAvailableDates, getInterventionAvailabilitiesForDay } from 'services/interventionAvailabilityServices';
import styled from 'styled-components';
import { InterventionSlotStatus } from 'ts-types/api.enums';
import {
  InterventionAvailabilityForDateRequest,
  InterventionAvailabilitySlotSearchDto,
  InterventionDto,
  InterventionSlotDto,
  ResourceDto
} from 'ts-types/api.types';
import { isoToDisplayDate, isoToDisplayTime, toIsoDateString } from 'utils/dateUtils';

const ContentDiv = styled.div`
  flex: 1 1 auto;
  height: 100%;
  display: flex;
  flex-direction: column;

  .main-container {
    display: grid;
    grid-template-columns: 45% 55%;
    column-gap: 1em;

    .availabilities-container {
      padding-top: .5rem;
    }
  }
`;

const StyledDayPicker = styled(DayPicker)`

  .DayPicker-Day {
    padding: 0.6em;
  }

  .DayPicker-Month {
    margin: unset;
  }

  .DayPicker-Day.DayPicker-Day--highlighted {
    position: relative;
  }

  .DayPicker-Day.DayPicker-Day--highlighted::before {
    content: "•";
    color: green;
    position: absolute;
    top: 0.92em;
    left: 0.6em;
    font-size: 1.3em;
    font-weight: 500;
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside)::before {
    color: #F0F8FF;
  }
`;

const StyledSlots = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px 10px 10px 0;
  overflow-y: auto;

  .available-slots-row {
    margin: 0.25rem 0 1rem;
    grid-column-start: 2;
    justify-self: start;

    .time-slot {
      display: inline-block;
      margin-right: 0.357rem;
      padding: 2px 4px;
      border-radius: 4px;
      cursor: default;
      border: 1px solid #e5e5e5;
      margin-bottom: 0.5rem;
    }

    .time-slot.BOOKED {
      background: #f1c6dc;
      border: 1px solid #d8a1bd;
      cursor: default;
    }

    .time-slot:not(.BOOKED):HOVER {
      background: #c4dcef;
    }
  }
`


interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  interventions: InterventionDto[],
  resources: ResourceDto[],
  onCancel: () => void
}

interface State {
  foundSlots: InterventionAvailabilitySlotSearchDto[],
  availableDates: Date[],
  disabledDates: Date[],
  interventionOptions: DropdownOption[];
  resourceOptions: DropdownOption[];
  loading: boolean;
  errorMessage?: string;
  cancelTokenSource: CancelTokenSource
}

class InterventionAvailabilitiesSearchView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {language} = this.props;

    this.state = {
      foundSlots: [],
      availableDates: [],
      disabledDates: [],
      interventionOptions: mapToDropdownOptionArrayWithTextGetter(props.interventions, intervention => {
        const descMap: { [lang: string]: string } = {
          "de": intervention.description,
          "en": intervention.descriptionEn || intervention.description,
          "fr": intervention.descriptionFr || intervention.description,
          "it": intervention.descriptionIt || intervention.description
        };
        return descMap[language]
      }),
      resourceOptions: mapToDropdownOptionArray(props.resources, "description"),
      loading: false,
      cancelTokenSource
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  getDaysArrayByMonth(date: Moment) {
    let daysInMonth = date.daysInMonth();
    let arrDays = [];

    while (daysInMonth) {
      const current = date.date(daysInMonth).toDate();
      arrDays.push(current);
      daysInMonth--;
    }

    return arrDays;
  }

  handleSelectDay = async (form: FormApi) => {

    const {getMandate} = this.props;
    const {cancelTokenSource} = this.state;

    const values = form.getState().values;

    if (values.interventionId && values.resourceId && values.date) {
      this.setState({loading: true, foundSlots: []});

      const request: InterventionAvailabilityForDateRequest = {
        mandateId: getMandate(),
        date: moment(values.date).format("YYYY-MM-DD"),
        interventionId: values.interventionId,
        resourceId: values.resourceId
      }

      try {
        const foundSlots: InterventionAvailabilitySlotSearchDto[] =
            await getInterventionAvailabilitiesForDay(request, request.date, cancelTokenSource);

        this.setState({
          foundSlots: foundSlots
        })
      } catch (e) {
        this.handleError(e.response.data);
      } finally {
        this.setState({
          loading: false
        })
      }
    }
  }

  onDropDownChange = async (form: FormApi) => {
    const {cancelTokenSource} = this.state;

    const values = form.getState().values;

    if (values.interventionId && values.resourceId && values.date) {
      this.setState({loading: true, foundSlots: []});

      const request: InterventionAvailabilityForDateRequest = {
        mandateId: this.props.getMandate(),
        date: moment(values.date).format("YYYY-MM-DD"),
        interventionId: values.interventionId,
        resourceId: values.resourceId
      }

      try {

        const daysOfMonth = this.getDaysArrayByMonth(moment(values.date));

        const availableDatesResponse = await getAvailableDates(request, cancelTokenSource);
        const availableDates = availableDatesResponse.map(date => moment(date).toDate());

        const disabledDates =
            _.differenceBy(daysOfMonth, availableDates, (date) => date.getDate());

        this.setState({
          availableDates: availableDates,
          disabledDates: disabledDates
        });
      } catch (e) {
        this.handleError(e.response.data);
      } finally {
        this.setState({
          loading: false
        })
      }
    }
  }

  resetSearchFormValues = (form: FormApi) => {

    form.change("interventionId", null);
    form.change("resourceId", null);

    this.setState({
      foundSlots: []
    })

  };

  render(): React.ReactNode {
    return (
        <ContentDiv>
          {this.renderSearchForm()}
        </ContentDiv>
    );
  };

  renderSearchForm = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{date: toIsoDateString(moment())}}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}
        />
    );
  };

  renderForm = ({form, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {onCancel, getMessage} = this.props;
    const {availableDates, disabledDates, interventionOptions, resourceOptions, foundSlots, loading} = this.state;

    const values = form.getState().values;
    const showCalendar = values.interventionId && values.resourceId;

    const modifiers = {
      highlighted: availableDates
    };

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="800px">
                  <div className="main-container">
                    <div className="form-fields">
                      <PxGrid>
                        <Grid.Row>
                          <Grid.Column width={7} verticalAlign="middle">
                            {getMessage("intervention.title")}:
                          </Grid.Column>
                          <Grid.Column width={9}>
                            <PxDropdownSc
                                value={values.interventionId}
                                onChange={(e: any, {value}: DropdownProps) => {
                                  form.change("interventionId", value);
                                  this.onDropDownChange(form);
                                }
                                }
                                options={interventionOptions}
                                selection
                                fluid
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={7} verticalAlign="middle">
                            {getMessage("patientCentral.overview.search.location")}:
                          </Grid.Column>
                          <Grid.Column width={9}>
                            <PxDropdownSc
                                value={values.resourceId}
                                onChange={(e: any, {value}: DropdownProps) => {
                                  form.change("resourceId", value);
                                  this.onDropDownChange(form);
                                }
                                }
                                options={resourceOptions}
                                selection
                                fluid
                            />
                          </Grid.Column>
                        </Grid.Row>
                        {showCalendar && !loading &&
                            <Grid.Row>
                              <Grid.Column width={16}>
                                <StyledDayPicker modifiers={modifiers}
                                                 disabledDays={disabledDates}
                                                 month={moment(values.date).toDate()}
                                                 selectedDays={[values.date]}
                                                 onMonthChange={month => {
                                                   form.change("date", month);
                                                   this.onDropDownChange(form);
                                                 }}
                                                 onDayClick={day => {
                                                   const dayWithAvailability =
                                                       availableDates.find(date => date.getDate() === day.getDate());
                                                   if (dayWithAvailability) {
                                                     form.change("date", day);
                                                     this.handleSelectDay(form);
                                                   }
                                                 }}
                                />
                              </Grid.Column>
                            </Grid.Row>
                        }
                      </PxGrid>
                    </div>
                    <div className="availabilities-container">
                      {foundSlots.length > 0 &&
                          <>
                            <Header as="h4">
                              {getMessage("header.navigation.interventionAvailabilityConfig")}
                            </Header>
                            {this.renderAvailabilities()}
                          </>
                      }
                    </div>
                    {loading && <LoaderComponent message={getMessage("loading")}/>}
                  </div>
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>
            <Modal.Actions>
              <Button
                  compact
                  type="button"
                  primary
                  onClick={() => this.handleSelectDay(form)}
              >
                {getMessage("button.refresh")}
              </Button>
              <Button
                  compact
                  type="button"
                  onClick={() => this.resetSearchFormValues(form)}
              >
                {getMessage("button.reset")}
              </Button>
              <Button
                  compact
                  type="button"
                  onClick={onCancel}
                  floated={'right'}
              >
                {getMessage("button.close")}
              </Button>
            </Modal.Actions>
          </FormContainer>
        </form>
    )
  };

  renderAvailabilities = () => {
    const {foundSlots} = this.state;

    return foundSlots.map(availability => <div key={availability.id}>
      <Header as="h4">{`${isoToDisplayDate(availability.date)}
      (${isoToDisplayTime(availability.fromTime)} - ${isoToDisplayTime(availability.toTime)})`}
      </Header>
      {this.renderAvailableSlots(availability.slots)}
    </div>)
  }

  renderAvailableSlots = (slots: InterventionSlotDto[]): JSX.Element => {

    const displayedSlots = slots.filter(slot => InterventionSlotStatus.BLOCKED !== slot.status);

    const slotNodes = displayedSlots.map((slotDto, ix) => {
          let newRow = false;
          const slot = moment(slotDto.fromTime, "HH:mm")

          const bookedClassname = slotDto.status ? slotDto.status : "";

          return <Fragment key={ix}>
            {newRow && <div className="new-row-div"/>}
            {ix > 0 && !newRow && <> </>}
            <div
                className={`time-slot ${bookedClassname}`}
            >
              {slot.format("HH:mm")}
            </div>
          </Fragment>
        }
    );

    return <StyledSlots>
      <div className={"available-slots-row"}>{slotNodes}</div>
    </StyledSlots>;
  };
}

export default withAuthContext(withMessages(InterventionAvailabilitiesSearchView));
